import {
  CAMPOS_PARAMETROS,
  type ChaveParametro,
  type CodigoGrupo,
  type Parametros,
  SECOES_PARAMETROS,
  type SecaoParametro,
  nomeGrupo,
  somaCustosKg,
  somaVariaveis,
  validarParametros,
} from '@precificador/shared/pricing.js';
import clsx from 'clsx';
import { type ReactNode, useId } from 'react';
import type { ParametrosPorGrupo } from '../api/tipos';
import { estiloEntrada } from './Campo';
import { formatarPct, formatarReaisKg, lerNumero, paraCampo } from '../lib/formatos';

/** Textos digitados, por grupo e campo (o formulário guarda texto; converte só ao enviar). */
export type TextosParametros = Partial<Record<CodigoGrupo, Record<ChaveParametro, string>>>;

export function paraTextos(grupos: CodigoGrupo[], parametros: ParametrosPorGrupo): TextosParametros {
  const r: TextosParametros = {};
  for (const g of grupos) {
    const p = parametros[g];
    r[g] = Object.fromEntries(CAMPOS_PARAMETROS.map((c) => [c.chave, p ? paraCampo(p[c.chave], 4) : ''])) as Record<ChaveParametro, string>;
  }
  return r;
}

/** Converte os textos; devolve os parâmetros e o primeiro erro por grupo. */
export function lerTextos(textos: TextosParametros): { parametros: ParametrosPorGrupo; erros: Partial<Record<CodigoGrupo, string>> } {
  const parametros: ParametrosPorGrupo = {};
  const erros: Partial<Record<CodigoGrupo, string>> = {};
  for (const [g, campos] of Object.entries(textos) as [CodigoGrupo, Record<ChaveParametro, string>][]) {
    const valores = Object.fromEntries(
      CAMPOS_PARAMETROS.map((c) => {
        const n = lerNumero(campos[c.chave] ?? '');
        return [c.chave, n === null || Number.isNaN(n) ? '' : n];
      }),
    );
    const erro = validarParametros(valores);
    if (erro) erros[g] = erro;
    else parametros[g] = valores as unknown as Parametros;
  }
  return { parametros, erros };
}

function Bloco({
  grupo,
  textos,
  aoMudar,
  info,
  mostrarErro,
}: {
  grupo: CodigoGrupo;
  textos: Record<ChaveParametro, string>;
  aoMudar: (chave: ChaveParametro, valor: string) => void;
  info?: ReactNode;
  mostrarErro: boolean;
}) {
  const id = useId();
  const numeros = Object.fromEntries(CAMPOS_PARAMETROS.map((c) => [c.chave, lerNumero(textos[c.chave] ?? '') ?? Number.NaN]));
  const custosKg = somaCustosKg(numeros);
  const pctVar = somaVariaveis(numeros);
  const erro = validarParametros(Object.fromEntries(CAMPOS_PARAMETROS.map((c) => [c.chave, Number.isNaN(numeros[c.chave]!) ? '' : numeros[c.chave]])));
  const total = pctVar + (numeros.margemPct ?? 0);

  // Total mostrado ao lado do título de cada bloco.
  const resumo: Record<SecaoParametro, ReactNode> = {
    custo: (
      <>
        total <strong className="numero font-semibold text-tinta">{Number.isFinite(custosKg) ? `R$ ${formatarReaisKg(custosKg)}/kg` : '—'}</strong>
      </>
    ),
    venda: (
      <>
        %variáveis <strong className="numero font-semibold text-tinta">{Number.isFinite(pctVar) ? formatarPct(pctVar) : '—'}</strong>
      </>
    ),
    margem: Number.isFinite(total) ? (
      <>
        %variáveis + margem <strong className={clsx('numero font-semibold', total >= 100 ? 'text-erro' : 'text-tinta')}>{formatarPct(total)}</strong>
      </>
    ) : null,
  };

  return (
    <fieldset className="rounded-painel border border-linha bg-superficie">
      <legend className="sr-only">{nomeGrupo(grupo)}</legend>
      <div className="border-b border-linha-suave px-4 py-3">
        <h3 className="font-semibold">{nomeGrupo(grupo)}</h3>
        {info && <p className="text-[13px] text-tinta-2">{info}</p>}
      </div>
      {SECOES_PARAMETROS.map((secao) => (
        <div key={secao.chave} className="border-b border-linha-suave px-4 py-3 last:border-0">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3">
            <p className="text-[13px] font-semibold">
              {secao.titulo}
              {secao.descricao && <span className="ml-1.5 font-normal text-tinta-2">{secao.descricao}</span>}
            </p>
            <p className="text-[13px] text-tinta-2">{resumo[secao.chave]}</p>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-3 sm:grid-cols-5">
            {CAMPOS_PARAMETROS.filter((c) => c.secao === secao.chave).map((c) => {
              const idCampo = `${id}-${c.chave}`;
              const valor = textos[c.chave] ?? '';
              const n = lerNumero(valor);
              const invalido = mostrarErro && (n === null || Number.isNaN(n));
              return (
                <div key={c.chave} className="flex flex-col justify-end gap-1">
                  <label htmlFor={idCampo} title={c.ajuda} className="text-[12px] leading-4 font-medium">
                    {c.rotulo.replace(/ (R\$\/kg|%)$/, '')}
                    {c.ajuda && <span className="block text-[11px] leading-[14px] font-normal text-tinta-2">{c.ajuda}</span>}
                  </label>
                  <div className="relative">
                    <input
                      id={idCampo}
                      inputMode="decimal"
                      value={valor}
                      onChange={(e) => aoMudar(c.chave, e.target.value)}
                      aria-invalid={invalido || undefined}
                      className={clsx(estiloEntrada, 'numero text-right', c.tipo === 'pct' ? 'pr-8' : 'pr-12', invalido ? 'border-erro' : 'border-linha')}
                    />
                    <span className="pointer-events-none absolute top-2 right-2.5 text-[12px] text-tinta-2">{c.tipo === 'pct' ? '%' : 'R$/kg'}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {mostrarErro && erro && <p className="px-4 pb-3 text-[13px] text-erro">{erro}</p>}
    </fieldset>
  );
}

/** Um bloco de parâmetros por grupo (SPEC §6.1), com o %variáveis somado em tempo real. */
export function FormParametros({
  grupos,
  textos,
  aoMudar,
  info,
  mostrarErros,
}: {
  grupos: CodigoGrupo[];
  textos: TextosParametros;
  aoMudar: (textos: TextosParametros) => void;
  info?: Partial<Record<CodigoGrupo, ReactNode>>;
  mostrarErros: boolean;
}) {
  return (
    <div className="flex flex-col gap-3">
      {grupos.map((g) => (
        <Bloco
          key={g}
          grupo={g}
          textos={textos[g] ?? ({} as Record<ChaveParametro, string>)}
          info={info?.[g]}
          mostrarErro={mostrarErros}
          aoMudar={(chave, valor) => aoMudar({ ...textos, [g]: { ...textos[g], [chave]: valor } })}
        />
      ))}
    </div>
  );
}
